import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import Swal from "sweetalert2";
import IsLogin from "../../../Common/IsLogin";
import ReportDateRangeCsv from "../../../Common/ReportDateRangeCSV";
import { ProgressSpinner } from "primereact/progressspinner";

const PackingExportReport = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const date = new Date();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const [inputs, setInputs] = useState({
    date_from: getCurrentDate(),
    date_to: getCurrentDate(),
  });
  const [loading, setLoading] = useState(false);

  function getCurrentDate() {
    const dateObj = new Date();
    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const fetchData = (dateFromParam, dateToParam) => {
    const dateFrom = dateFromParam || inputs.date_from || getCurrentDate();
    const dateTo = dateToParam || inputs.date_to || getCurrentDate();
    setLoading(true);
    Http.get(
      `/production-and-transfer-list?date_from=${dateFrom}&date_to=${dateTo}`,
    )
      .then((res) => {
        setReceiveData(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const formSubmit = () => {
    fetchData(inputs.date_from, inputs.date_to);
  };

  const calculateRowValues = (item) => {
    const receiveDataArr = item.receive_data || [];
    const productionData = item.production_data || [];
    const transferData = item.transfer_data || [];

    const receivedExport = receiveDataArr.reduce(
      (sum, r) => sum + Number(r.receive_qty_export || 0),
      0,
    );

    const transferredExport = transferData.reduce(
      (sum, t) =>
        sum +
        (t.is_return === 0 || t.is_return === undefined
          ? Number(t.delivery_qty_export || 0)
          : 0),
      0,
    );
    const transferredInner = transferData.reduce(
      (sum, t) =>
        sum +
        (t.is_return === 0 || t.is_return === undefined
          ? Number(t.delivery_inner_qty || 0)
          : 0),
      0,
    );
    const transferredMaster = transferData.reduce(
      (sum, t) =>
        sum +
        (t.is_return === 0 || t.is_return === undefined
          ? Number(t.delivery_master_qty || 0)
          : 0),
      0,
    );

    const lossExport = productionData.reduce(
      (sum, p) => sum + Number(p.loss_qty_export || 0),
      0,
    );
    const lossInner = productionData.reduce(
      (sum, p) => sum + Number(p.loss_qty_inner_qty || 0),
      0,
    );
    const lossMaster = productionData.reduce(
      (sum, p) => sum + Number(p.loss_qty_master_qty || 0),
      0,
    );

    return {
      receivedExport,
      transferredExport,
      transferredInner,
      transferredMaster,
      lossExport,
      lossInner,
      lossMaster,
    };
  };

  const totals = receiveData.reduce(
    (acc, item) => {
      const {
        receivedExport,
        transferredExport,
        transferredInner,
        transferredMaster,
        lossExport,
        lossInner,
        lossMaster,
      } = calculateRowValues(item);

      return {
        totalReceivedExport: acc.totalReceivedExport + receivedExport,
        totalTransferredExport: acc.totalTransferredExport + transferredExport,
        totalTransferredInner: acc.totalTransferredInner + transferredInner,
        totalTransferredMaster: acc.totalTransferredMaster + transferredMaster,
        totalLossExport: acc.totalLossExport + lossExport,
        totalLossInner: acc.totalLossInner + lossInner,
        totalLossMaster: acc.totalLossMaster + lossMaster,
      };
    },
    {
      totalReceivedExport: 0,
      totalTransferredExport: 0,
      totalTransferredInner: 0,
      totalTransferredMaster: 0,
      totalLossExport: 0,
      totalLossInner: 0,
      totalLossMaster: 0,
    },
  );

  const getCsvData = () => {
    const headers = [
      "Name of Ware",
      "Shape",
      "Glaze",
      "Decoration Code",
      "Decoration Name",
      "Received Export",
      "Transferred Export",
      "Transferred Inner",
      "Transferred Master",
      "Loss Export",
      "Loss Inner",
      "Loss Master",
    ];

    const rows = receiveData.map((item) => {
      const {
        receivedExport,
        transferredExport,
        transferredInner,
        transferredMaster,
        lossExport,
        lossInner,
        lossMaster,
      } = calculateRowValues(item);
      return [
        item.product_name,
        item.shape,
        item.glaze,
        item.decoration_code,
        item.decoration_name,
        receivedExport,
        transferredExport,
        transferredInner,
        transferredMaster,
        lossExport,
        lossInner,
        lossMaster,
      ];
    });

    const footerRows = [
      [
        "",
        "",
        "",
        "",
        "Total",
        totals.totalReceivedExport,
        totals.totalTransferredExport,
        totals.totalTransferredInner,
        totals.totalTransferredMaster,
        totals.totalLossExport,
        totals.totalLossInner,
        totals.totalLossMaster,
      ],
    ];

    return { headers, rows, footerRows };
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Packing Export Report</h5>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />
            <div className="card pd-20 pd-sm-40">
              <ReportDateRangeCsv
                dateFrom={inputs.date_from}
                dateTo={inputs.date_to}
                onChange={handleChange}
                onSearch={formSubmit}
                csvFilename={`packing-export-report_${inputs.date_from}_to_${inputs.date_to}.csv`}
                getCsvData={getCsvData}
              />
            </div>
          </div>
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              <div className="row">
                <div className="col-12">
                  <div className="text-center">
                    <h5 className="text-uppercase text-sm font-weight-bolder">
                      Artisan Ceramics Limited
                    </h5>
                    <p>
                      House-16, Road-07, Sector-01, Uttara, Dhaka
                      <br />
                      <b>Packing Export Report</b>
                    </p>
                  </div>
                  <div
                    className="table-responsive p-0 pb-2"
                    style={{ maxHeight: "70vh", overflowY: "auto" }}
                  >
                    {" "}
                    <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0 _datatable">
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          backgroundColor: "#fff",
                        }}
                      >
                        <tr>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            rowSpan={2}
                          >
                            Name of Ware
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Shape
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Glaze
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Decoration Code
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            rowSpan={2}
                          >
                            Decoration Name
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            colSpan={1}
                          >
                            Received
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            colSpan={3}
                          >
                            Transferred
                          </th>
                          <th
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                            colSpan={3}
                          >
                            Loss
                          </th>
                        </tr>
                        <tr>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Export
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Export
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Inner
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Master
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Export
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Inner
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Master
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td
                              className="text-center"
                              colSpan={12}
                              style={{ padding: "2rem" }}
                            >
                              <ProgressSpinner
                                style={{ width: "40px", height: "40px" }}
                                strokeWidth="4"
                              />
                            </td>
                          </tr>
                        ) : receiveData && receiveData.length > 0 ? (
                          receiveData.map((item, index) => {
                            const {
                              receivedExport,
                              transferredExport,
                              transferredInner,
                              transferredMaster,
                              lossExport,
                              lossInner,
                              lossMaster,
                            } = calculateRowValues(item);

                            return (
                              <tr key={index}>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.product_name}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.shape}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.glaze}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.decoration_code}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.decoration_name}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {receivedExport}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {transferredExport}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {transferredInner}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {transferredMaster}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {lossExport}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {lossInner}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {lossMaster}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                              colSpan={5 + 1 + 3 + 3}
                            >
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            colSpan={5}
                          >
                            Total
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalReceivedExport}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTransferredExport}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTransferredInner}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTransferredMaster}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalLossExport}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalLossInner}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalLossMaster}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackingExportReport;
